import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { finalize, first, map, takeUntil } from 'rxjs/operators';
import { WorkerService } from 'src/app/data/worker.service';
import { EducationHistoryFilesDto } from 'src/app/models/dtos/education-history-files-dto';
import { FileService } from 'src/app/shared/services/file-service';
import { ModuleName } from 'src/app/subscription-package';
import { WorkerFileVerificationStatusEnum } from 'src/app/common/enums';
import { AddEducationHistoryFileComponent, AddEducationHistoryFileData } from '../add-education-history-file/add-education-history-file.component';

@Component({
  selector: 'app-education-history-documents-section',
  templateUrl: './education-history-documents-section.component.html',
  styleUrls: ['./education-history-documents-section.component.scss'],
  standalone: false,
})
export class EducationHistoryDocumentsSectionComponent implements OnInit, OnDestroy {
  private dialog = inject(MatDialog);
  private workerService = inject(WorkerService);
  private spinner = inject(NgxSpinnerService);
  private fileService = inject(FileService);

  @Input() workerId: number;
  @Input() readonly = false;
  @Input() canAddEducationDocument = true;

  readonly moduleNames = ModuleName;
  readonly workerFileVerificationStatusEnum = WorkerFileVerificationStatusEnum;

  private allFiles$ = new BehaviorSubject<EducationHistoryFilesDto[]>([]);
  filesWithDocuments$: Observable<EducationHistoryFilesDto[]> = this.allFiles$.pipe(
    map((files) => files.filter((f) => !!f.Files?.length)),
  );

  private unsubscribe$ = new Subject<void>();

  ngOnInit(): void {
    this.loadFiles();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.allFiles$.complete();
  }

  private loadFiles(): void {
    this.spinner.show();
    this.workerService
      .getWorkerProfileDocuments(this.workerId)
      .pipe(
        first(),
        finalize(() => this.spinner.hide()),
        takeUntil(this.unsubscribe$),
      )
      .subscribe((docs) => {
        this.allFiles$.next(docs.EducationHistoryFiles);
      });
  }

  openModal(): void {
    this.dialog
      .open(AddEducationHistoryFileComponent, {
        data: <AddEducationHistoryFileData>{
          Title: 'EducationHistoryDocuments',
          WorkerId: this.workerId,
          EducationHistoryFiles: this.allFiles$.getValue(),
        },
      })
      .afterClosed()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((isSuccess: boolean) => {
        if (isSuccess){
          this.loadFiles();
        }
      });
  }

  showFile(id: number, name: string): void {
    this.fileService.showFile(name, id, `workers/${this.workerId}/files`);
  }

  downloadFile(id: number, name: string): void {
    this.fileService.downloadFile(id, name, `workers/${this.workerId}/files`);
  }
}
